import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ExternalLink, Printer } from "lucide-react";
import { api, trackEvent } from "../api";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "../components/StatusBadge";
import { ProvenanceBlock } from "../components/ProvenanceBlock";
import { Button } from "../components/ui/button";

// PG-HYDRO-001 analytics (owner gate §10): fires once when a report actually
// renders the hydrology section. Aggregate counter only — no identifiers.
function HydroIncludedPing() {
  useEffect(() => { trackEvent("hydrology_report_included"); }, []);
  return null;
}

export default function ReportPage() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);

  useEffect(() => {
    if (user === null) return;
    if (user === false) { navigate("/logowanie"); return; }
    api.get(`/reports/${id}`).then((r) => setReport(r.data)).catch(() => setReport(false));
  }, [id, user, navigate]);

  if (!user || report === null) return <div className="p-20 text-center text-slate-500">{t("common.loading")}</div>;
  if (report === false) return <div className="p-20 text-center text-slate-500">404</div>;

  const d = report.data;
  const pid = d.parcel_identification;
  const ps = d.planning_status;

  return (
    <div data-testid="report-page" className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
      <div className="no-print mb-6 flex justify-end">
        <Button data-testid="report-print-button" onClick={() => window.print()} className="bg-slate-900 text-white hover:bg-slate-800">
          <Printer size={15} className="mr-2" /> {t("report.print")}
        </Button>
      </div>

      <article className="report-page rounded-xl border bg-white p-8 shadow-sm sm:p-12">
        <header className="border-b-2 border-slate-900 pb-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-heading text-xs font-bold uppercase tracking-widest text-[#DC143C]">PlanGmina</p>
              <h1 className="mt-2 font-heading text-3xl font-extrabold tracking-tight">
                {t("report.title")} — {pid.number}
              </h1>
              <p className="mt-2 text-sm text-slate-600">
                {t("report.generated")}: {new Date(report.created_at).toLocaleString("pl-PL")} · {t("auth.role")}: {user.role}
              </p>
            </div>
            {d.data_kind === "official" ? (
              <span data-testid="report-official-badge" className="rounded border border-emerald-400 bg-emerald-50 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-emerald-700">
                {t("data.official")}
              </span>
            ) : d.is_demo && (
              <span className="rounded border border-dashed border-slate-400 bg-slate-50 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                {d.demo_label}
              </span>
            )}
          </div>
        </header>

        <section className="mt-8">
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t("report.identification")}</h2>
          <dl className="mt-3 grid grid-cols-2 gap-x-8 gap-y-2 text-sm sm:grid-cols-3">
            <div><dt className="text-xs text-slate-600">{t("pro.table.parcel")}</dt><dd className="font-mono-data font-semibold">{pid.number}</dd></div>
            <div><dt className="text-xs text-slate-600">Adres / Address</dt><dd>{pid.address}</dd></div>
            <div><dt className="text-xs text-slate-600">{t("common.gmina")}</dt><dd>{pid.gmina}</dd></div>
            <div><dt className="text-xs text-slate-600">{t("common.powiat")}</dt><dd>{pid.powiat}</dd></div>
            <div><dt className="text-xs text-slate-600">{t("common.woj")}</dt><dd>{pid.wojewodztwo}</dd></div>
            <div><dt className="text-xs text-slate-600">{t("parcel.area")}</dt><dd>{pid.area_m2?.toLocaleString()} m²</dd></div>
          </dl>
        </section>

        <section className="mt-8">
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t("report.status")}</h2>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-lg border p-3">
              <p className="text-[10px] uppercase tracking-wide text-slate-400">{t("parcel.planOgolny")}</p>
              <div className="mt-1.5"><StatusBadge status={ps.plan_ogolny_status} label={t(`status.${ps.plan_ogolny_status}`)} /></div>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-[10px] uppercase tracking-wide text-slate-400">{t("parcel.mpzp")}</p>
              <div className="mt-1.5"><StatusBadge status={ps.mpzp_status} label={t(`mpzp.${ps.mpzp_status}`)} /></div>
            </div>
            {ps.development_certainty && (
            <div className="rounded-lg border p-3">
              <p className="text-[10px] uppercase tracking-wide text-slate-400">{t("parcel.certainty")}</p>
              <p className="mt-1.5 text-sm font-bold">{t(`cert.${ps.development_certainty}`)}</p>
            </div>
            )}
            {ps.change_risk && (
            <div className="rounded-lg border p-3">
              <p className="text-[10px] uppercase tracking-wide text-slate-400">{t("parcel.risk")}</p>
              <p className="mt-1.5 text-sm font-bold">{t(`risk.${ps.change_risk}`)}</p>
            </div>
            )}
          </div>
          {ps.planning_zone && (
            <p className="mt-3 text-sm text-slate-700"><span className="font-semibold">{t("parcel.zone")}:</span> {ps.planning_zone}</p>
          )}
          {ps.mpzp_name && <p className="mt-1 text-xs text-slate-500">{ps.mpzp_name}</p>}
        </section>

        {(() => {
          const f = d.due_diligence?.flood;
          if (!f) return null;
          return (
            <section data-testid="report-flood-section" className="mt-8">
              <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t("flood.title")}</h2>
              <div className="mt-3">
                <StatusBadge status={f.status} label={lang === "pl" ? f.badge_pl : f.badge_en} testId="report-flood-badge" />
              </div>
              {(f.status === "DETECTED" || f.status === "NOT_DETECTED") && (
                <dl className="mt-3 grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                  <div><dt className="text-xs text-slate-600">{t("flood.zones.hazard")}</dt><dd className="font-semibold" data-testid="flood-hazard-hits">{f.hazard_area_hits}</dd></div>
                  <div><dt className="text-xs text-slate-600">{t("flood.zones.risk")}</dt><dd className="font-semibold" data-testid="flood-risk-hits">{f.risk_zone_hits}</dd></div>
                </dl>
              )}
              <p className="mt-3 text-sm text-slate-700" data-testid="flood-interpretation">{lang === "pl" ? f.interpretation_pl : f.interpretation_en}</p>
              <ProvenanceBlock p={f.provenance} t={t} testId="flood-provenance" />
            </section>
          );
        })()}

        {(() => {
          const h = d.due_diligence?.hydro;
          if (!h || !h.summary) return null;
          return (
            <section data-testid="report-hydro-section" className="mt-8">
              <HydroIncludedPing />
              <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t("hydro.title")}</h2>
              <div className="mt-3">
                <span data-testid="report-hydro-level" className="inline-block rounded-full px-3 py-1 text-xs font-bold bg-slate-900 text-white">
                  {t("hydro.exposure")}: {lang === "pl" ? h.summary.level_pl : h.summary.level_en}
                </span>
                <p className="mt-1 text-[10px] uppercase tracking-widest text-slate-400">{t("hydro.evidence.derived")}</p>
              </div>
              {h.exposure && (
                <div className="mt-2" data-testid="report-hydro-exposure">
                  <p className="text-sm font-bold">{lang === "pl" ? h.exposure.badge_pl : h.exposure.badge_en}</p>
                  <p className="mt-1 text-xs text-slate-500">{h.exposure.caveat_pl}</p>
                </div>
              )}
              <p className="mt-3 text-xs font-bold uppercase tracking-widest text-slate-500">{t("hydro.why")}</p>
              <ul className="mt-1 list-disc pl-4 text-sm text-slate-700 space-y-1" data-testid="report-hydro-reasons">
                {h.summary.reasons.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
              {(h.flood?.flood_findings || []).length > 0 && (
                <div className="mt-3" data-testid="report-hydro-findings">
                  <p className="text-[10px] uppercase tracking-widest text-slate-400">{t("hydro.evidence.official")} + {t("hydro.evidence.derived")}</p>
                  {h.flood.flood_findings.map((fl, i) => (
                    <dl key={i} className="mt-2 grid grid-cols-2 gap-x-8 gap-y-2 text-sm" data-testid={`report-hydro-finding-${i}`}>
                      <div><dt className="text-xs text-slate-600">{fl.layer_pl}</dt><dd className="font-semibold">{fl.scenario_oficjalny || "—"}</dd></div>
                      <div><dt className="text-xs text-slate-600">{t("hydro.affected_area")} / {t("hydro.affected_pct")}</dt><dd className="font-semibold">{Math.round(fl.intersection_area_m2).toLocaleString()} m² · {fl.intersection_percent}%</dd></div>
                    </dl>
                  ))}
                </div>
              )}
              {h.water_summary && (h.water_summary.intersects_parcel > 0 || h.water_summary.nearest_water_m != null) && (
                <p className="mt-3 text-sm text-slate-700" data-testid="report-hydro-water">
                  <span className="text-[10px] uppercase tracking-widest text-slate-400 block">{t("hydro.evidence.official")} — MPHP10k</span>
                  {h.water_summary.intersects_parcel > 0
                    ? t("hydro.crosses")
                    : `${t("hydro.nearest_water")}: ${h.water_summary.nearest_water_m} m`}
                </p>
              )}
              {h.catchment?.nazwy?.length > 0 && (
                <p className="mt-2 text-sm text-slate-700" data-testid="report-hydro-catchment">{t("hydro.catchment")}: {h.catchment.nazwy.join(", ")}</p>
              )}
              {h.imgw_context?.status === "OK" && h.imgw_context.station && (
                <div className="mt-3 text-sm text-slate-700" data-testid="report-hydro-imgw">
                  <span className="text-[10px] uppercase tracking-widest text-slate-400 block">{t("hydro.evidence.contextual")}</span>
                  <p>{t("hydro.station")}: {h.imgw_context.station.nazwa}{h.imgw_context.station.rzeka ? ` (${h.imgw_context.station.rzeka})` : ""} — {h.imgw_context.station.odleglosc_km} km</p>
                  {h.imgw_context.station.stan_wody_cm != null && (
                    <p className="text-xs text-slate-500">{h.imgw_context.station.stan_wody_cm} cm · {h.imgw_context.station.pomiar_at}</p>
                  )}
                  <p className="text-xs italic text-slate-500">{t("hydro.context_note")}</p>
                  {(h.imgw_context.warnings || []).map((w, i) => (
                    <p key={i} className="mt-1 text-xs" data-testid={`report-hydro-warning-${i}`}>{t("hydro.warnings")}: {w.zdarzenie} — {lang === "pl" ? "stopień" : "level"} {w.stopien} ({w.data_od} → {w.data_do})</p>
                  ))}
                </div>
              )}
              <p className="mt-3 text-xs text-slate-500" data-testid="report-hydro-disclaimer">{h.summary.disclaimer_pl}</p>
              {(h.source_errors || []).length > 0 && (
                <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="report-hydro-source-errors">
                  <p className="font-bold uppercase tracking-widest">{t("hydro.sources_unavailable")}</p>
                  <ul className="mt-1 list-disc pl-4">
                    {h.source_errors.map((e, i) => <li key={i} data-testid={`report-hydro-source-error-${i}`}>{e.component}: {e.error}</li>)}
                  </ul>
                </div>
              )}
              {(h.sources || []).filter(Boolean).map((p, i) => (
                <ProvenanceBlock key={i} p={p} t={t} testId={`hydro-provenance-${i}`} />
              ))}
            </section>
          );
        })()}

        {(() => {
          const ia = d.imagery_archive;
          if (!ia || !ia.status) return null;
          return (
            <section data-testid="report-imagery-archive" className="mt-8">
              <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">
                {lang === "pl" ? "Zobrazowania archiwalne" : "Archival imagery"}
              </h2>
              <p className="mt-2 text-sm font-semibold" data-testid="imagery-archive-summary">
                {lang === "pl" ? `Zobrazowania archiwalne: ${ia.summary_pl || ia.badge_pl}`
                               : (ia.summary_en || ia.badge_pl)}
              </p>
              {ia.period_count > 0 && (
                <details className="mt-2" data-testid="imagery-archive-years">
                  <summary className="cursor-pointer text-xs text-emerald-700 underline underline-offset-2">
                    {lang === "pl" ? "Pokaż dostępne okresy i proweniencję" : "Show available periods and provenance"}
                  </summary>
                  <ul className="mt-2 grid grid-cols-3 gap-1 text-xs sm:grid-cols-6">
                    {ia.periods.map((p) => (
                      <li key={p.year} data-testid={`imagery-year-${p.year}`}>
                        <span className="font-semibold">{p.year}</span>
                        {p.gsd_m ? ` · ${p.gsd_m} m` : ""}
                        {p.render === "DOWNLOAD_ONLY" && (
                          <span className="text-slate-500">{lang === "pl" ? " · tylko indeks" : " · index only"}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {ia.note_pl && lang === "pl" && (
                <p className="mt-2 text-xs text-slate-500" data-testid="imagery-archive-note">{ia.note_pl}</p>
              )}
              <ProvenanceBlock p={ia.provenance} t={t} testId="imagery-archive-provenance" />
            </section>
          );
        })()}

        {(() => {
          const tw = d.due_diligence?.terrain_water;
          if (!tw) return null;
          const fmt = (v, suf) => (v === null || v === undefined ? "—" : `${v}${suf}`);
          return (
            <section data-testid="report-terrain-section" className="mt-8">
              <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t("terrain.title")}</h2>
              <div className="mt-3">
                <StatusBadge status={tw.status === "DETECTED" ? "unknown" : tw.status} label={lang === "pl" ? tw.badge_pl : tw.badge_en} testId="report-terrain-badge" />
              </div>
              {tw.status === "DETECTED" && (
                <dl className="mt-3 grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                  <div><dt className="text-xs text-slate-600">{t("terrain.min")} / {t("terrain.max")}</dt><dd className="font-semibold" data-testid="terrain-minmax">{fmt(tw.min_elevation_m, "")}–{fmt(tw.max_elevation_m, " m n.p.m.")}</dd></div>
                  <div><dt className="text-xs text-slate-600">{t("terrain.mean")}</dt><dd className="font-semibold" data-testid="terrain-mean">{fmt(tw.mean_elevation_m, " m n.p.m.")}</dd></div>
                  <div><dt className="text-xs text-slate-600">{t("terrain.range")}</dt><dd className="font-semibold" data-testid="terrain-range">{fmt(tw.elevation_range_m, " m")}</dd></div>
                  <div><dt className="text-xs text-slate-600">{t("terrain.slope")}</dt><dd className="font-semibold" data-testid="terrain-slope">{fmt(tw.slope_pct, "%")}</dd></div>
                  <div><dt className="text-xs text-slate-600">{t("terrain.aspect")}</dt><dd className="font-semibold" data-testid="terrain-aspect">{tw.dominant_aspect_pl || "—"}</dd></div>
                  <div><dt className="text-xs text-slate-600">{t("terrain.rel50")} / 100 / 250 m</dt><dd className="font-semibold" data-testid="terrain-rel">{fmt(tw.rel_elevation_50m_m, "")} / {fmt(tw.rel_elevation_100m_m, "")} / {fmt(tw.rel_elevation_250m_m, " m")}</dd></div>
                  <div><dt className="text-xs text-slate-600">{t("terrain.ponding")}</dt><dd className="font-semibold" data-testid="terrain-ponding">{tw.ponding_status || "—"}{tw.ponding_zones ? ` (${tw.ponding_zones})` : ""}</dd></div>
                  <div><dt className="text-xs text-slate-600">{t("terrain.edges")}</dt><dd className="font-semibold" data-testid="terrain-edges">{fmt(tw.entry_edges, "")} / {fmt(tw.exit_edges, "")}</dd></div>
                  <div><dt className="text-xs text-slate-600">{t("terrain.catchment")}</dt><dd className="font-semibold" data-testid="terrain-catchment">{tw.catchment_m2 ? `${tw.catchment_m2} m²` : "—"}</dd></div>
                  <div><dt className="text-xs text-slate-600">{t("terrain.zones")}</dt><dd className="font-semibold" data-testid="terrain-zones">{tw.lowest_zone_pl || "—"} / {tw.highest_zone_pl || "—"}</dd></div>
                </dl>
              )}
              <p className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700" data-testid="terrain-note">{lang === "pl" ? tw.note_pl : tw.note_en}</p>
              <p className="mt-3 text-sm text-slate-700" data-testid="terrain-interpretation">{lang === "pl" ? tw.interpretation_pl : tw.interpretation_en}</p>
              <ProvenanceBlock p={tw.provenance} t={t} testId="terrain-provenance" />
            </section>
          );
        })()}

        {(() => {
          const r = d.due_diligence?.planning_registry;
          if (!r) return null;
          const acts = r.acts || [];
          return (
            <section data-testid="report-ru-section" className="mt-8">
              <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t("ru.title")}</h2>
              <p className="mt-2 text-sm font-semibold" data-testid="ru-label">{lang === "pl" ? r.label_pl : r.label_en}</p>
              {acts.length > 0 && (
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="text-left text-slate-500">
                      <th className="py-1 pr-3">{t("ru.col.act")}</th>
                      <th className="py-1 pr-3">{t("ru.col.type")}</th>
                      <th className="py-1 pr-3">{t("ru.col.status")}</th>
                      <th className="py-1">{t("ru.col.valid_from")}</th>
                    </tr></thead>
                    <tbody>
                      {acts.slice(0, 10).map((a) => (
                        <tr key={a.id} className="border-t border-slate-100 align-top">
                          <td className="py-1.5 pr-3">
                            {a.details_url ? (
                              <a href={a.details_url} target="_blank" rel="noreferrer" className="text-emerald-700 underline" data-testid={`ru-act-${a.id}`}>{a.title}</a>
                            ) : (
                              <span data-testid={`ru-act-${a.id}`}>{a.title}</span>
                            )}
                          </td>
                          <td className="py-1.5 pr-3">{a.plan_type_pl}</td>
                          <td className="py-1.5 pr-3">{a.legal_status || "—"}</td>
                          <td className="py-1.5">{a.valid_from || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {typeof r.acts_total === "number" && r.acts_total > 10 && (
                    <p className="mt-1 text-xs text-slate-500">{t("ru.more")}: {r.acts_total - 10}</p>
                  )}
                </div>
              )}
              <p className="mt-2 text-xs text-slate-500" data-testid="ru-transition-note">{lang === "pl" ? r.transition_note_pl : r.transition_note_en}</p>
              <p className="mt-3 text-sm text-slate-700" data-testid="ru-interpretation">{lang === "pl" ? r.interpretation_pl : r.interpretation_en}</p>
              <ProvenanceBlock p={r.provenance} t={t} testId="ru-provenance" />
            </section>
          );
        })()}

        {(() => {
          const h = d.due_diligence?.heritage;
          if (!h) return null;
          const items = h.findings || [];
          return (
            <section data-testid="report-heritage-section" className="mt-8">
              <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t("heritage.title")}</h2>
              <div className="mt-3">
                <StatusBadge status={h.status} label={lang === "pl" ? h.badge_pl : h.badge_en} testId="report-heritage-badge" />
              </div>
              {items.length > 0 && (
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="text-left text-slate-500">
                      <th className="py-1 pr-3">{t("heritage.col.object")}</th>
                      <th className="py-1 pr-3">{t("heritage.col.register")}</th>
                      <th className="py-1 pr-3">{t("heritage.col.rel")}</th>
                      <th className="py-1 pr-3">{t("heritage.col.dist")}</th>
                      <th className="py-1">{t("heritage.col.docs")}</th>
                    </tr></thead>
                    <tbody>
                      {items.slice(0, 12).map((f, i) => (
                        <tr key={f.inspire_id || i} className="border-t border-slate-100 align-top">
                          <td className="py-1.5 pr-3">
                            {f.details_url ? (
                              <a href={f.details_url} target="_blank" rel="noreferrer" className="text-emerald-700 underline" data-testid={`heritage-obj-${i}`}>{f.name || "—"}</a>
                            ) : (
                              <span data-testid={`heritage-obj-${i}`}>{f.name || t("data.none")}</span>
                            )}
                          </td>
                          <td className="py-1.5 pr-3">{f.register_pl}</td>
                          <td className="py-1.5 pr-3">
                            <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${f.relationship === "INTERSECTS" ? "bg-amber-100 text-amber-800" : "bg-sky-100 text-sky-800"}`}>
                              {f.relationship === "INTERSECTS" ? t("heritage.rel.intersects") : t("heritage.rel.nearby")}
                            </span>
                          </td>
                          <td className="py-1.5 pr-3">{f.distance_m != null ? `${t("heritage.dist.approx")} ${f.distance_m} m` : "—"}</td>
                          <td className="py-1.5">{f.legal_doc || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="heritage-note">{lang === "pl" ? h.note_pl : h.note_en}</p>
              <p className="mt-3 text-sm text-slate-700" data-testid="heritage-interpretation">{lang === "pl" ? h.interpretation_pl : h.interpretation_en}</p>
              {h.provenance?.failed_registers && (
                <p className="mt-2 text-xs text-slate-500" data-testid="heritage-partial">{t("heritage.partial")}</p>
              )}
              <ProvenanceBlock p={h.provenance} t={t} testId="heritage-provenance" />
            </section>
          );
        })()}

        {(() => {
          const el = d.due_diligence?.elevation;
          if (!el) return null;
          return (
            <section data-testid="report-elevation-section" className="mt-8">
              <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t("elevation.title")}</h2>
              <div className="mt-3">
                <StatusBadge status={el.status === "DETECTED" ? "unknown" : el.status} label={lang === "pl" ? el.badge_pl : el.badge_en} testId="report-elevation-badge" />
              </div>
              {el.status === "DETECTED" && el.elevation_m != null && (
                <dl className="mt-3 grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                  <div><dt className="text-xs text-slate-600">{t("elevation.value")}</dt><dd className="font-semibold" data-testid="elevation-value">{el.elevation_m.toFixed(1)} m n.p.m.</dd></div>
                  <div><dt className="text-xs text-slate-600">{t("elevation.datum")}</dt><dd className="font-semibold" data-testid="elevation-datum">{el.vertical_datum}</dd></div>
                </dl>
              )}
              <p className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700" data-testid="elevation-note">{lang === "pl" ? el.note_pl : el.note_en}</p>
              <p className="mt-3 text-sm text-slate-700" data-testid="elevation-interpretation">{lang === "pl" ? el.interpretation_pl : el.interpretation_en}</p>
              <ProvenanceBlock p={el.provenance} t={t} testId="elevation-provenance" />
            </section>
          );
        })()}

        {(() => {
          const s = d.due_diligence?.soils;
          if (!s) return null;
          const contours = s.contours || [];
          return (
            <section data-testid="report-soils-section" className="mt-8">
              <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t("soils.title")}</h2>
              <div className="mt-3">
                <StatusBadge status={s.status === "DETECTED" ? "unknown" : s.status} label={lang === "pl" ? s.badge_pl : s.badge_en} testId="report-soils-badge" />
              </div>
              {contours.length > 0 && (
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="text-left text-slate-500">
                      <th className="py-1 pr-3">{t("soils.col.complex")}</th>
                      <th className="py-1 pr-3">{t("soils.col.type")}</th>
                      <th className="py-1 pr-3">{t("soils.col.substrate")}</th>
                      <th className="py-1 pr-3">{t("soils.col.version")}</th>
                      <th className="py-1">{t("soils.col.source")}</th>
                    </tr></thead>
                    <tbody>
                      {contours.slice(0, 10).map((c, i) => (
                        <tr key={c.lokalny_id || i} className="border-t border-slate-100 align-top">
                          <td className="py-1.5 pr-3" data-testid={`soils-contour-${i}`}>
                            {c.kompleks ? `${c.kompleks}${c.kompleks_label_pl ? ` — ${c.kompleks_label_pl}` : ""}` : t("data.none")}
                          </td>
                          <td className="py-1.5 pr-3">{c.typ_podtyp || "—"}</td>
                          <td className="py-1.5 pr-3">{c.podloze?.length ? c.podloze.join(", ") : "—"}</td>
                          <td className="py-1.5 pr-3">{c.wersja || "—"}</td>
                          <td className="py-1.5">{c.zrodlo || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="soils-note">{lang === "pl" ? s.note_pl : s.note_en}</p>
              <p className="mt-3 text-sm text-slate-700" data-testid="soils-interpretation">{lang === "pl" ? s.interpretation_pl : s.interpretation_en}</p>
              <ProvenanceBlock p={s.provenance} t={t} testId="soils-provenance" />
            </section>
          );
        })()}

        <section className="mt-8">
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t("evidence.facts")}</h2>
          <div className="mt-3 overflow-x-auto">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="bg-slate-900 text-left text-white">
                <th className="px-3 py-2">Fakt</th>
                <th className="px-3 py-2">Wartość</th>
                <th className="px-3 py-2">{t("report.source")}</th>
                <th className="px-3 py-2">{t("report.version")}</th>
                <th className="px-3 py-2">{t("report.verification")}</th>
              </tr>
            </thead>
            <tbody>
              {d.evidence_table.map((e, i) => (
                <tr key={i} className="border-b align-top">
                  <td className="px-3 py-2 font-medium">{lang === "pl" ? e.fact_pl : e.fact_en}</td>
                  <td className="px-3 py-2">{lang === "pl" ? e.value_pl : e.value_en}</td>
                  <td className="px-3 py-2 text-slate-600">{e.source_authority}</td>
                  <td className="px-3 py-2 font-mono-data text-slate-600">{e.document_version}</td>
                  <td className="px-3 py-2">{e.verification_status}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </section>

        {d.sources?.length > 0 && (
          <section className="mt-8">
            <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t("report.provenance")}</h2>
            <table data-testid="report-provenance-table" className="mt-3 hidden w-full border-collapse text-xs sm:table">
              <thead>
                <tr className="bg-slate-900 text-left text-white">
                  <th className="px-3 py-2">{t("report.source")}</th>
                  <th className="px-3 py-2">URL</th>
                  <th className="px-3 py-2">{t("real.retrieved")}</th>
                </tr>
              </thead>
              <tbody>
                {d.sources.map((s, i) => (
                  <tr key={i} data-testid={`report-source-row-${i}`} className="border-b align-top">
                    <td className="px-3 py-2 font-medium">{s.authority}</td>
                    <td className="px-3 py-2">
                      <a href={s.url} target="_blank" rel="noreferrer" data-testid={`report-source-link-${i}`} className="inline-flex items-center gap-1 font-semibold text-emerald-700 hover:underline">
                        <ExternalLink size={12} /> {t("report.openSource")}
                      </a>
                    </td>
                    <td className="px-3 py-2 font-mono-data text-slate-600">{(s.retrieved_at || "").slice(0, 19).replace("T", " ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-3 space-y-2 sm:hidden" data-testid="report-provenance-cards">
              {d.sources.map((s, i) => (
                <div key={i} data-testid={`report-source-card-${i}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                  <p className="font-medium text-slate-800">{s.authority}</p>
                  <p className="mt-1"><span className="font-semibold">{t("real.retrieved")}:</span> <span className="font-mono-data">{(s.retrieved_at || "").slice(0, 19).replace("T", " ")}</span></p>
                  <a href={s.url} target="_blank" rel="noreferrer" data-testid={`report-source-card-link-${i}`} className="mt-2 inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1 font-semibold text-emerald-700 hover:bg-emerald-50">
                    <ExternalLink size={12} /> {t("report.openSource")}
                  </a>
                </div>
              ))}
            </div>
          </section>
        )}

        {d.ai_interpretation ? (
        <section className="mt-8 rounded-lg border border-indigo-200 bg-indigo-50/40 p-5">
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-indigo-700">{t("evidence.ai")}</h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-800">
            {lang === "pl" ? d.ai_interpretation?.text_pl : d.ai_interpretation?.text_en}
          </p>
          <p className="mt-2 text-[11px] text-slate-500">{t("evidence.ai.note")}</p>
        </section>
        ) : (
        <section className="mt-8 rounded-lg border border-slate-200 bg-slate-50 p-5">
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t("evidence.noai.title")}</h2>
          <p data-testid="report-no-ai" className="mt-2 text-sm text-slate-600">{t("report.noai")}</p>
        </section>
        )}

        <section className="mt-8 rounded-lg border border-rose-200 bg-rose-50/40 p-5">
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-rose-700">{t("evidence.human")}</h2>
          <p className="mt-2 text-sm text-slate-700">{lang === "pl" ? d.human_review.note_pl : d.human_review.note_en}</p>
        </section>

        {d.important_dates.length > 0 && (
          <section className="mt-8">
            <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t("report.dates")}</h2>
            <ul className="mt-3 space-y-1.5 text-sm">
              {d.important_dates.map((x, i) => (
                <li key={i} className="flex gap-3">
                  <span className="font-mono-data text-xs text-slate-500">{x.date}</span>
                  <span>{lang === "pl" ? x.label_pl : x.label_en}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="mt-8">
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t("report.documents")}</h2>
          {d.documents.length === 0 && (
            <p data-testid="report-documents-none" className="mt-3 text-sm text-slate-500">{t("data.none")}</p>
          )}
          <ul className="mt-3 space-y-2 text-sm">
            {d.documents.map((doc) => (
              <li key={doc.id} className="rounded-lg border px-4 py-2.5">
                <p className="font-medium">{doc.title}</p>
                <p className="text-xs text-slate-500">{doc.authority} · {doc.publication_date} · {doc.status}</p>
              </li>
            ))}
          </ul>
        </section>

        <footer className="mt-10 border-t pt-5">
          <div data-testid="report-integrity-footer" className="mb-5 rounded-lg bg-slate-50 p-4 font-mono-data text-[11px] leading-relaxed text-slate-600">
            <p data-testid="report-integrity-id"><span className="font-bold">{t("report.id")}:</span> PG-{new Date(report.created_at).getFullYear()}-{report.id.slice(0, 8).toUpperCase()}</p>
            <p><span className="font-bold">{t("report.generated")}:</span> {new Date(report.created_at).toLocaleString("pl-PL", { timeZone: "UTC" })} UTC</p>
            <p data-testid="report-integrity-sources-at"><span className="font-bold">{t("report.sourcesAt")}:</span> {d.retrieved_at ? `${d.retrieved_at.slice(0, 19).replace("T", " ")} UTC` : "—"}</p>
            {report.report_hash && (
              <p data-testid="report-integrity-hash" className="break-all"><span className="font-bold">{t("report.hash")}:</span> {report.report_hash}</p>
            )}
          </div>
          <p data-testid="report-disclaimer" className="text-xs leading-relaxed text-slate-500">
            {lang === "pl" ? report.disclaimer_pl : report.disclaimer_en}
          </p>
        </footer>
      </article>
    </div>
  );
}
